import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStaffAuth } from '../context/StaffAuthContext';
import { 
  Calendar, Clock, MapPin, ChevronLeft, ChevronRight, 
  LogOut, User, Home, CheckCircle2, AlertCircle, Loader2, Bell, X, Star, Volume2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification sound - using Web Audio API for a pleasant chime
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create a pleasant two-tone chime
    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    playTone(880, now, 0.15);        // A5
    playTone(1108.73, now + 0.15, 0.2); // C#6
    
  } catch (error) {
    console.log('Audio not supported');
  }
};

const StaffDashboard = () => {
  const { staff, token, logout, isAuthenticated, loading: authLoading } = useStaffAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month'); // 'month' or 'day'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [newJobsCount, setNewJobsCount] = useState(0);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const notificationRef = useRef(null);
  const previousUnreadCount = useRef(0);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/staff/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Memoized fetch functions
  const fetchJobs = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/staff/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs || []);
      setNewJobsCount(response.data.new_jobs_count || 0);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/staff/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newNotifications = response.data.notifications || [];
      const newUnreadCount = response.data.unread_count || 0;
      
      // Play sound if there are new unread notifications
      if (soundEnabled && newUnreadCount > previousUnreadCount.current && previousUnreadCount.current > 0) {
        playNotificationSound();
      }
      
      previousUnreadCount.current = newUnreadCount;
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [token, soundEnabled]);

  useEffect(() => {
    // Only fetch if authenticated and not still loading
    if (!authLoading && isAuthenticated && token) {
      fetchJobs();
      fetchNotifications();
      
      // Poll for new notifications every 30 seconds
      const pollInterval = setInterval(() => {
        fetchNotifications();
      }, 30000);
      
      return () => clearInterval(pollInterval);
    }
  }, [authLoading, isAuthenticated, token, fetchJobs, fetchNotifications]);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/staff/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.patch(`${API}/staff/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markNotificationRead(notification.id);
    }
    // Navigate to the job
    navigate(`/staff/jobs/${notification.job_id}`);
    setShowNotifications(false);
  };

  const markJobViewed = async (jobId) => {
    try {
      await axios.post(`${API}/staff/jobs/${jobId}/mark-viewed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update local state
      setJobs(prev => prev.map(job => 
        job.id === jobId ? { ...job, staff_viewed: true } : job
      ));
      setNewJobsCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking job as viewed:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/staff/login');
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Adjust for Monday as first day of week (0=Monday, 6=Sunday)
    // getDay() returns 0 for Sunday, so we convert: Sunday(0)->6, Monday(1)->0, etc.
    const startingDay = (firstDay.getDay() + 6) % 7;
    
    const days = [];
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const formatDateKey = (date) => {
    if (!date) return '';
    // Use local date to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getJobsForDate = (date) => {
    if (!date) return [];
    const dateKey = formatDateKey(date);
    return jobs.filter(job => job.preferred_date === dateKey);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleDateClick = (date) => {
    if (date) {
      setSelectedDate(date);
      setView('day');
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
    setView('day');
  };

  const isAirbnb = (job) => job?.source === 'airbnb' || /airbnb/i.test(job?.service_type || '');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'accepted': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'accepted': return 'Scheduled';
      default: return 'Pending';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#1a5c3a] mx-auto mb-4" size={40} />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated - show loading while redirecting
  if (!isAuthenticated || !token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#1a5c3a] mx-auto mb-4" size={40} />
          <p className="text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  // Week starts on Monday (European/Portuguese style)
  const dayNames = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

  const days = getDaysInMonth(currentDate);
  const todayJobs = selectedDate ? getJobsForDate(selectedDate) : getJobsForDate(new Date());

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1a5c3a] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                alt="Sancur Services" 
                className="h-12 w-auto brightness-0 invert"
              />
              <span className="text-white font-heading text-xl font-bold tracking-tight hidden sm:inline">
                Sancur Services
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Notifications Dropdown */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative flex items-center gap-2 px-2 sm:px-3 py-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="notifications-btn"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications Panel */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border z-50 max-h-[28rem] overflow-hidden">
                    <div className="flex items-center justify-between p-3 border-b bg-gray-50">
                      <h3 className="font-semibold text-gray-900">Notificações</h3>
                      <div className="flex items-center gap-3">
                        {/* Sound toggle */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setSoundEnabled(!soundEnabled); }}
                          className={`p-1.5 rounded-lg transition-colors ${soundEnabled ? 'text-primary bg-primary/10' : 'text-gray-400 bg-gray-100'}`}
                          title={soundEnabled ? 'Som ativado' : 'Som desativado'}
                        >
                          <Volume2 size={16} />
                        </button>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllNotificationsRead}
                            className="text-xs text-primary hover:underline"
                          >
                            Marcar lidas
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-center text-gray-500 text-sm">
                          Nenhuma notificação
                        </p>
                      ) : (
                        notifications.slice(0, 20).map(notification => {
                          // Determine icon and colors based on notification type
                          const getNotificationStyle = () => {
                            if (notification.type === 'job_cancelled') {
                              return { bg: 'bg-red-100', text: 'text-red-700', icon: <X size={16} /> };
                            }
                            if (notification.type === 'job_rescheduled') {
                              return { bg: 'bg-orange-100', text: 'text-orange-700', icon: <Calendar size={16} /> };
                            }
                            if (notification.is_team_leader || notification.type === 'team_leader_assigned') {
                              return { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: <Star size={16} /> };
                            }
                            return { bg: 'bg-green-100', text: 'text-green-700', icon: <Home size={16} /> };
                          };
                          
                          const style = getNotificationStyle();
                          
                          return (
                            <div
                              key={notification.id}
                              onClick={() => handleNotificationClick(notification)}
                              className={`p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                                !notification.read ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${style.bg} ${style.text}`}>
                                  {style.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                    {notification.message}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                  {notification.job_date && (
                                    <span className="flex items-center gap-1">
                                      <Calendar size={12} />
                                      {new Date(notification.job_date).toLocaleDateString('pt-PT')}
                                    </span>
                                  )}
                                  <span>
                                    {new Date(notification.timestamp).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              </div>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                              )}
                            </div>
                          </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => navigate('/staff/profile')}
                className="flex items-center gap-2 text-sm hover:bg-white/10 px-2 sm:px-3 py-2 rounded-lg transition-colors"
                data-testid="staff-profile-btn"
              >
                {staff?.profile_photo ? (
                  <img src={staff.profile_photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <User size={18} />
                )}
                <span className="hidden sm:inline">{staff?.name || 'Profile'}</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-2 sm:px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                data-testid="staff-logout-btn"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* View Toggle & Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setView('month')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                view === 'month' 
                  ? 'bg-[#1a5c3a] text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="view-month-btn"
            >
              <Calendar size={18} className="inline mr-2" />
              Month
            </button>
            <button
              onClick={() => { setView('day'); if (!selectedDate) setSelectedDate(new Date()); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                view === 'day' 
                  ? 'bg-[#1a5c3a] text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid="view-day-btn"
            >
              <Clock size={18} className="inline mr-2" />
              Day
            </button>
          </div>

          <button
            onClick={goToToday}
            className="px-4 py-2 bg-[#d4af37] text-white rounded-lg font-medium hover:bg-[#c9a432] transition-colors"
            data-testid="go-today-btn"
          >
            Today
          </button>
          <button
            onClick={() => navigate('/staff/hours')}
            className="px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
            data-testid="view-hours-btn"
          >
            <Clock size={18} />
            My Hours
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Today&apos;s Jobs</p>
                <p className="text-xl font-bold text-gray-900">
                  {getJobsForDate(new Date()).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="text-yellow-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-xl font-bold text-gray-900">
                  {jobs.filter(j => j.status === 'pending' || j.status === 'accepted').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-xl font-bold text-gray-900">
                  {jobs.filter(j => j.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MapPin className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Jobs</p>
                <p className="text-xl font-bold text-gray-900">{jobs.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Month View */}
        {view === 'month' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Month Navigation */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="prev-month-btn"
              >
                <ChevronLeft size={24} />
              </button>
              <h2 className="text-xl font-bold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="next-month-btn"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            <div className="flex flex-wrap gap-3 px-4 py-2 text-xs text-gray-600 border-b bg-white" data-testid="staff-calendar-legend">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-rose-400"></span> Airbnb</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-yellow-300"></span> Pending</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-300"></span> Scheduled</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-300"></span> In progress</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-300"></span> Completed</span>
            </div>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {/* Day Headers */}
              {dayNames.map(day => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gray-500 border-b bg-gray-50">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {days.map((day, index) => {
                const dayJobs = day ? getJobsForDate(day) : [];
                const isToday = day && formatDateKey(day) === formatDateKey(new Date());
                const isSelected = day && selectedDate && formatDateKey(day) === formatDateKey(selectedDate);
                const hasNewJobs = dayJobs.some(job => !job.staff_viewed && job.status !== 'completed');
                
                return (
                  <div
                    key={index}
                    onClick={() => handleDateClick(day)}
                    className={`min-h-[100px] p-2 border-b border-r cursor-pointer transition-colors relative
                      ${day ? 'hover:bg-gray-50' : 'bg-gray-50'}
                      ${isToday ? 'bg-green-50' : ''}
                      ${isSelected ? 'ring-2 ring-[#1a5c3a] ring-inset' : ''}
                      ${hasNewJobs ? 'bg-yellow-50' : ''}
                    `}
                    data-testid={day ? `calendar-day-${formatDateKey(day)}` : undefined}
                  >
                    {day && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium
                            ${isToday ? 'bg-[#1a5c3a] text-white' : 'text-gray-700'}
                          `}>
                            {day.getDate()}
                          </span>
                          {hasNewJobs && (
                            <span className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" title="New job"></span>
                          )}
                        </div>
                        {dayJobs.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {dayJobs.slice(0, 2).map((job) => (
                              <div
                                key={job.id}
                                className={`text-xs p-1 rounded truncate ${isAirbnb(job) && job.status !== 'completed' ? 'bg-rose-100 text-rose-700 border border-rose-200' : getStatusColor(job.status)} ${
                                  !job.staff_viewed && job.status !== 'completed' ? 'ring-2 ring-yellow-400' : ''
                                }`}
                                data-testid={isAirbnb(job) ? 'staff-cal-job-airbnb' : 'staff-cal-job'}
                              >
                                {isAirbnb(job) ? '🏡 ' : ''}{job.preferred_time} - {job.service_type}
                              </div>
                            ))}
                            {dayJobs.length > 2 && (
                              <div className="text-xs text-gray-500 pl-1">
                                +{dayJobs.length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Day View */}
        {view === 'day' && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Day Navigation */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <button
                onClick={() => {
                  const newDate = new Date(selectedDate || new Date());
                  newDate.setDate(newDate.getDate() - 1);
                  setSelectedDate(newDate);
                }}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="prev-day-btn"
              >
                <ChevronLeft size={24} />
              </button>
              <h2 className="text-xl font-bold text-gray-900">
                {(selectedDate || new Date()).toLocaleDateString('en-IE', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h2>
              <button
                onClick={() => {
                  const newDate = new Date(selectedDate || new Date());
                  newDate.setDate(newDate.getDate() + 1);
                  setSelectedDate(newDate);
                }}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="next-day-btn"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Jobs List */}
            <div className="p-4">
              {todayJobs.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
                  <p className="text-gray-500">No jobs scheduled for this day</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {todayJobs.map((job) => (
                    <div
                      key={job.id}
                      onClick={() => {
                        if (!job.staff_viewed && job.status !== 'completed') {
                          markJobViewed(job.id);
                        }
                        navigate(`/staff/job/${job.id}`);
                      }}
                      className={`p-4 border rounded-xl hover:shadow-md transition-all cursor-pointer relative ${
                        isAirbnb(job) ? 'bg-rose-50/60 border-l-4 border-l-rose-500' : 'bg-white'
                      } ${
                        !job.staff_viewed && job.status !== 'completed' ? 'border-yellow-400 border-2' : ''
                      }`}
                      data-testid={`job-card-${job.id}`}
                    >
                      {isAirbnb(job) && (
                        <span className="absolute top-2 right-8 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-500 text-white" data-testid="staff-job-airbnb-badge">🏡 Airbnb</span>
                      )}
                      {/* New Badge */}
                      {!job.staff_viewed && job.status !== 'completed' && (
                        <div className="absolute -top-2 -right-2 px-2 py-1 bg-yellow-500 text-yellow-900 text-xs font-bold rounded-full">
                          NEW
                        </div>
                      )}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(job.status)}`}>
                              {getStatusText(job.status)}
                            </span>
                            <span className="text-sm text-gray-500">
                              <Clock size={14} className="inline mr-1" />
                              {job.preferred_time}
                            </span>
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-1">{job.service_type}</h3>
                          <p className="text-sm text-gray-600 mb-2">{job.name}</p>
                          <div className="flex items-center text-sm text-gray-500">
                            <MapPin size={14} className="mr-1" />
                            <span className="truncate">{job.address || job.eircode || 'No address'}</span>
                          </div>
                        </div>
                        <ChevronRight className="text-gray-400" size={20} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default StaffDashboard;
